import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import {toast} from "react-toastify";
import {Tab, TabList, TabPanel, Tabs} from "react-tabs";
import ReactTable from "../../Components/ReactTable";
import {LoadingWrapper} from "../../Components/LoadingWrapper";
import {makeApiCallWithoutCatch} from "../../../Api/makeApiCall";
import CsvDownload from "../../../utils/CsvDownloadWithNulls";
import {faIcons} from "../../../utils/iconConstants";
import {toastError} from "../../../utils/toast";

export const STORE_CHANGE_FEEDBACK_DAGS = [
    'edp_projection_spins_stores',
    'edp_projection_csg_stores',
    'edp_projection_manual_stores',
];

const DISPLAY_LIMIT = 5000;
const DOWNLOAD_LIMIT = 100000;

const SOURCE_COUNTS = [
    {key: 'records_processed', label: 'Records Processed'},
    {key: 'added', label: 'Added'},
    {key: 'updated', label: 'Updated'},
    {key: 'closed', label: 'Closed'},
];

const CANONICAL_COUNTS = [
    {key: 'added', label: 'Added'},
    {key: 'updated', label: 'Updated'},
    {key: 'closed', label: 'Closed'},
    {key: 'merged', label: 'Merged'},
    {key: 'removed', label: 'Removed'},
    {key: 'winner_changed', label: 'Winner Changed'},
    {key: 'links_changed', label: 'Links Changed'},
];

const feedbackUrl = () => `${process.env.REACT_APP_EDP_API_BASE_URL}/projectionStores/feedback/`;

const feedbackErrorMessage = (apiError, fallback) => apiError?.response?.status === 404
    ? apiError.response.data?.detail ?? 'No change feedback is available for this run.'
    : fallback;

const formatValue = (value) => {
    if (value === null || value === undefined) return '—';
    if (Array.isArray(value)) return value.join(', ');
    return String(value);
};

const formatRecord = (record) => record?.source_name ? `${record.source_name} ${record.source_store_id}` : '—';

const attributeLines = (changes) =>
    (changes ?? []).map(change => `${change.attribute}: ${formatValue(change.previous)} → ${formatValue(change.new)}`);

const linkLines = (linkChanges) => [
    ...(linkChanges?.linked ?? []).map(record => `+ ${formatRecord(record)}`),
    ...(linkChanges?.unlinked ?? []).map(record => `− ${formatRecord(record)}`),
];

const winnerChangeLine = (winnerChange) =>
    winnerChange ? `${formatRecord(winnerChange.previous)} → ${formatRecord(winnerChange.new)}` : '';

export const toSourceRows = (changes) => (changes ?? []).map(change => ({
    source: change.source_name,
    sourceStoreId: change.source_store_id,
    changeType: change.change_type,
    changes: attributeLines(change.changes),
}));

export const toCanonicalRows = (changes) => (changes ?? []).map(change => ({
    storeId: change.store_id,
    winner: formatRecord(change.winner),
    changeType: change.change_type,
    winnerChange: winnerChangeLine(change.winner_change),
    links: linkLines(change.link_changes),
    attributeChanges: attributeLines(change.attribute_changes),
    mergedInto: (change.merged_into ?? []).join(', '),
}));

const toCsvRows = (rows) => rows.map(row => Object.fromEntries(
    Object.entries(row).map(([key, value]) => [key, Array.isArray(value) ? value.join('; ') : value])
));

const Lines = ({lines}) => <>{lines.map((line, index) => <div key={index}>{line}</div>)}</>;

Lines.propTypes = {lines: PropTypes.arrayOf(PropTypes.string).isRequired};

const linesCell = ({getValue}) => <Lines lines={getValue()}/>;

const linesFilter = (row, columnId, filterValue) =>
    !filterValue || row.getValue(columnId).join(' ').toLowerCase().includes(String(filterValue).toLowerCase());

const SOURCE_COLUMNS = [
    {header: 'Source', accessorKey: 'source', className: 'non-numeric-field'},
    {header: 'Source Store ID', accessorKey: 'sourceStoreId', className: 'non-numeric-field'},
    {header: 'Change Type', accessorKey: 'changeType', className: 'non-numeric-field'},
    {header: 'Changes', accessorKey: 'changes', className: 'non-numeric-field', cell: linesCell, filterFn: linesFilter},
];

const CANONICAL_COLUMNS = [
    {header: 'Store ID', accessorKey: 'storeId', className: 'non-numeric-field'},
    {header: 'Current Winner', accessorKey: 'winner', className: 'non-numeric-field'},
    {header: 'Change Type', accessorKey: 'changeType', className: 'non-numeric-field'},
    {header: 'Winner Change', accessorKey: 'winnerChange', className: 'non-numeric-field'},
    {header: 'Links', accessorKey: 'links', className: 'non-numeric-field', cell: linesCell, filterFn: linesFilter},
    {
        header: 'Attribute Changes',
        accessorKey: 'attributeChanges',
        className: 'non-numeric-field',
        cell: linesCell,
        filterFn: linesFilter
    },
    {header: 'Merged Into', accessorKey: 'mergedInto', className: 'non-numeric-field'},
];

const SECTIONS = {
    source: {label: 'Source Stores', counts: SOURCE_COUNTS, columns: SOURCE_COLUMNS, toRows: toSourceRows},
    canonical: {label: 'Canonical Stores', counts: CANONICAL_COUNTS, columns: CANONICAL_COLUMNS, toRows: toCanonicalRows},
};

const fetchSection = (dagId, runId, section, pageSize) =>
    makeApiCallWithoutCatch(feedbackUrl(), {dagId, runId, section, pageSize});

const FeedbackSection = ({dagId, runId, section, response}) => {
    const [downloading, setDownloading] = useState(false);
    const {counts, columns, toRows} = SECTIONS[section];
    const summary = response.summary?.[section] ?? {};
    const rows = toRows(response.changes);

    const onDownload = () => {
        setDownloading(true);
        fetchSection(dagId, runId, section, DOWNLOAD_LIMIT)
            .then(fullResponse => CsvDownload(
                toCsvRows(toRows(fullResponse.changes)),
                `${dagId}_${runId}_${section}_changes.csv`,
                ''
            ))
            .catch(apiError => toastError(
                toast,
                feedbackErrorMessage(apiError, 'The change feedback CSV could not be downloaded. Please try again.')
            ))
            .finally(() => setDownloading(false));
    };

    return (
        <div data-testid={`store-change-feedback-${section}`}>
            <div className={"store-change-feedback-summary"}>
                {counts.map(({key, label}) => (
                    <span key={key} data-testid={`store-change-feedback-${section}-count-${key}`}>
                        {label}: {summary[key] ?? 0}
                    </span>
                ))}
            </div>
            {response.total > rows.length &&
                <div className={"store-change-feedback-note"}>
                    {response.total > DOWNLOAD_LIMIT
                        ? `Showing the first ${rows.length} of ${response.total} changes. The CSV holds only the first ${DOWNLOAD_LIMIT}.`
                        : `Showing the first ${rows.length} of ${response.total} changes. Download the CSV for all of them.`}
                </div>
            }
            <ReactTable className={"store-change-feedback-table"}
                        filterable
                        data={rows}
                        columns={columns}
                        initialState={{pagination: {pageSize: 10}}}/>
            <button type={"button"}
                    className={"hollow button secondary"}
                    disabled={downloading || response.total === 0}
                    onClick={onDownload}>
                {downloading ? 'Downloading...' : 'Download CSV'}
            </button>
        </div>
    );
};

FeedbackSection.propTypes = {
    dagId: PropTypes.string.isRequired,
    runId: PropTypes.string.isRequired,
    section: PropTypes.oneOf(Object.keys(SECTIONS)).isRequired,
    response: PropTypes.shape({
        summary: PropTypes.object,
        total: PropTypes.number,
        changes: PropTypes.array,
    }).isRequired,
};

const StoreChangeFeedbackModal = ({dagId, runId, onClose}) => {
    const [responses, setResponses] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        setResponses(null);
        setError(null);
        Promise.all(Object.keys(SECTIONS).map(section => fetchSection(dagId, runId, section, DISPLAY_LIMIT)))
            .then(([source, canonical]) => setResponses({source, canonical}))
            .catch(apiError => setError(
                feedbackErrorMessage(apiError, 'Change feedback could not be loaded. Please try again.')
            ));
    }, [dagId, runId]);

    const header = responses?.source;

    return (
        <Modal
            isOpen={true}
            className="Modal store-change-feedback-modal"
            overlayClassName="Overlay"
            ariaHideApp={false}
            onRequestClose={onClose}
            testId={"store-change-feedback-modal"}
        >
            <div className={"modal-header"}>
                <div>
                    <h5 className={"modal-header-text"}>Store Change Feedback</h5>
                    <div className={"store-change-feedback-run"} data-testid={"store-change-feedback-run"}>
                        {header ? `${header.source_name} · version ${header.version} · ${runId}` : runId}
                    </div>
                </div>
                <div>
                    <button className={"right-exit-button"} onClick={onClose} aria-label={"Close"}>
                        <i className={faIcons.xClose}/></button>
                </div>
            </div>
            {error
                ? <div className={"store-change-feedback-error"} data-testid={"store-change-feedback-error"}>{error}</div>
                : <LoadingWrapper loading={!responses}>
                    {responses &&
                        <Tabs>
                            <TabList>
                                {Object.entries(SECTIONS).map(([section, {label}]) =>
                                    <Tab key={section}>{label} ({responses[section].total})</Tab>)}
                            </TabList>
                            {Object.keys(SECTIONS).map(section =>
                                <TabPanel key={section}>
                                    <FeedbackSection dagId={dagId} runId={runId} section={section}
                                                     response={responses[section]}/>
                                </TabPanel>
                            )}
                        </Tabs>
                    }
                </LoadingWrapper>
            }
        </Modal>
    );
};

StoreChangeFeedbackModal.propTypes = {
    dagId: PropTypes.string.isRequired,
    runId: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default StoreChangeFeedbackModal;
